import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player';

import { ArrowLink } from '../../Common/Links';
import { ROTATE_RATE } from './Carousel';
import * as styles from './Carousel.module.scss';
import CarouselListItem from './CarouselListItem';

const VideoCarousel = ({
  videoUrl = '',
  items = [],
  heading,
  eyebrowText,
  accentColor,
  flipped,
  backgroundColor,
  onCta,
  ctaUrl,
  ctaLabel,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showTimerProgress, setShowTimerProgress] = useState(true);
  const [beginTimer, setBeginTimer] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const timerRef = useRef(null);

  const onTick = useCallback(() => {
    setBeginTimer(false);
    setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
    setBeginTimer(true);
  }, [items.length]);

  useEffect(() => {
    if (inView) {
      setBeginTimer(true);

      timerRef.current = setInterval(() => {
        onTick();
      }, ROTATE_RATE);
    }

    return () => clearInterval(timerRef.current);
  }, [inView, onTick]);

  const onSelect = (index) => {
    setActiveIndex(index);
    setBeginTimer(false);
    setShowTimerProgress(false);
    clearInterval(timerRef.current);
  };

  const carouselItem = items.map((item, i) => {
    const isSelected = i === activeIndex;

    return (
      <CarouselListItem
        key={i}
        contentful_id={item.contentful_id}
        heading={item.title}
        description={item.copy}
        showTimerProgress={showTimerProgress}
        isSelected={isSelected}
        rotateRate={ROTATE_RATE}
        begin={beginTimer}
        onSelect={() => onSelect(i)}
      />
    );
  });

  return (
    <>
      <div className={styles.heading}>
        {eyebrowText && (
          <div className={[styles.eyebrowText, accentColor ? styles[accentColor] : '', 'themed-eyebrow'].join(' ')}>
            {eyebrowText}
          </div>
        )}
        <h2>{heading}</h2>
      </div>
      <div
        ref={ref}
        className={[styles.carousel, flipped ? styles.flipped : '', styles[backgroundColor]].join(' ')}
        data-analytics-section={'video-carousel'}
      >
        <div className={styles.image}>
          <HydrationProvider>
            <Client>
              <ReactPlayer url={videoUrl} controls={true} width="100%" />
            </Client>
          </HydrationProvider>
        </div>
        <div className={styles.content}>
          <ul>{carouselItem}</ul>
          {ctaLabel && !ctaUrl && (
            <div className={styles.cta}>
              <ArrowLink label={ctaLabel} onClick={onCta} />
            </div>
          )}
          {ctaLabel && ctaUrl && (
            <div className={styles.cta}>
              <ArrowLink label={ctaLabel} url={ctaUrl} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoCarousel;
